// EditListingPage.jsx

// EditListingPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, Grid, Divider } from '@mui/material';
import useFetchListings from '../../../hooks/useFetchListings';
import useFirebaseUpload from '../../../hooks/useFirebaseUpload';
import EditListingPageStyles from './EditListingPage.styles';
import PropertyLockManager from '../../dashboard/PropertyLockManager';

const EditListingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { singleListing, loading } = useFetchListings(id);
  const { uploadProperty } = useFirebaseUpload();

  const [formData, setFormData] = useState({
    price: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    description: '',
    deposit: '',
    applicationFee: '',
    specialInstructions: '',
  });

  const [images, setImages] = useState([]); // → existing images only (read-only)

  /* ───────────────────── load listing ───────────────────── */
  useEffect(() => {
    if (singleListing) {
      // Extract ZIP from address using regex, fallback to singleListing.zip if available
      const zipMatch = singleListing.address?.match(/\b\d{5}(?:-\d{4})?\b/);
      setFormData({
        price: singleListing.rent || '',
        address: singleListing.address?.split(',')[0]?.trim() || '',
        city: singleListing.city || '',
        state: singleListing.state || '',
        zip: zipMatch ? zipMatch[0] : singleListing.zip || '',
        description: singleListing.description || '',
        deposit: singleListing.deposit || '',
        applicationFee: singleListing.applicationFee || '',
        specialInstructions: singleListing.specialInstructions || '',
      });
      setImages(singleListing.images || []);
    }
  }, [singleListing]);

  /* ──────────────────── handlers ──────────────────── */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow up to 5 numbers for zip
    if (name === 'zip') {
      if (!/^\d{0,5}$/.test(value)) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      const updatedListing = {
        ...singleListing,
        id,
        rent: formData.price,
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        description: formData.description,
        deposit: formData.deposit,
        applicationFee: formData.applicationFee,
        specialInstructions: formData.specialInstructions,
        images,
      };
      

      await uploadProperty(updatedListing, [], null); // no files to upload
      alert('Listing updated successfully');
      navigate('/agent-dashboard');
    } catch (error) {
      console.error('Error updating listing:', error);
      alert('Failed to update listing');
    }
  };

  /* ─────────────────────── UI ─────────────────────── */
  return (
    <Box sx={EditListingPageStyles.container}>
      <Paper sx={EditListingPageStyles.paper}>
        <Typography variant="h4" sx={EditListingPageStyles.title}>
          Edit Listing
        </Typography>

        {loading ? (
          <Typography>Loading listing...</Typography>
        ) : singleListing ? (
          <form onSubmit={handleSubmit}>
            {/* -------- text fields -------- */}
            <TextField
              label="Price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="Street Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="State"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="ZIP Code"
              name="zip"
              value={formData.zip}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]{5}',
                maxLength: 5,
              }}
              helperText="ZIP code must be 5 digits."
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="Deposit"
              name="deposit"
              value={formData.deposit}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="Application Fee"
              name="applicationFee"
              value={formData.applicationFee}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              sx={EditListingPageStyles.textField}
            />
            <TextField
              label="Special Instructions"
              name="specialInstructions"
              value={formData.specialInstructions}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              margin="normal"
              sx={EditListingPageStyles.textField}
            />

            {/* -------- read-only image thumbnails -------- */}
            {images.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Photos
                </Typography>
                <Grid container spacing={2}>
                  {images.map((url, index) => (
                    <Grid item xs={3} key={index}>
                      <img
                        src={url}
                        alt={`img-${index}`}
                        style={{ width: '100%', borderRadius: 4 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* -------- Smart Lock Management -------- */}
            <Divider sx={{ my: 4 }} />
            {id && (
              <PropertyLockManager
                listingId={id}
                propertyInfo={{
                  address: formData.address,
                  city: formData.city,
                  state: formData.state,
                }}
              />
            )}

            {/* -------- submit -------- */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={EditListingPageStyles.submitButton}
            >
              Save Changes
            </Button>
          </form>
        ) : (
          <Typography>Listing not found.</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default EditListingPage;
